import * as React from 'react';

import { useRosterIdForContract } from 'bundles/enterprise-admin-learners/hooks/useRosterIdForContract';

export type ContractScopedSubmitHandler = (groupId: string, rosterId?: string) => void | Promise<void>;

type UseContractScopedSubmitParams = {
  /** Set for multi-contract orgs once ContractSelectionStep has run. */
  selectedContractId?: string;
};

/**
 * Shared "resolve this group's roster under `selectedContractId`, then submit (or show
 * IncompatibleContractGroupModal on no match)" state machine — used by both CsvInviteModal
 * and EmailInviteModal. Each call site passes its own submit logic in per request via
 * `requestSubmit`'s second argument, since EmailInviteModal has two distinct submit paths
 * (initial invite vs. the post-send group-assignment step) while CsvInviteModal has one.
 */
export const useContractScopedSubmit = ({ selectedContractId }: UseContractScopedSubmitParams) => {
  const [rosterLookupGroupId, setRosterLookupGroupId] = React.useState<string | undefined>(undefined);
  const [showIncompatibleContractModal, setShowIncompatibleContractModal] = React.useState(false);
  // Captures the caller's submit function for the in-flight request — a ref (not state)
  // since it's write-once-read-once per request and shouldn't itself trigger a re-render.
  const pendingSubmitRef = React.useRef<ContractScopedSubmitHandler | null>(null);
  const {
    rosterId: resolvedRosterId,
    loading: rosterLoading,
    hasNoMatch,
  } = useRosterIdForContract(rosterLookupGroupId, selectedContractId);
  const isResolvingRoster = Boolean(rosterLookupGroupId);

  // Resolve the contract-scoped roster (if any) before the mutation call fires.
  React.useEffect(() => {
    if (!rosterLookupGroupId || rosterLoading) return;
    const groupId = rosterLookupGroupId;
    const onResolved = pendingSubmitRef.current;
    setRosterLookupGroupId(undefined);
    pendingSubmitRef.current = null;
    if (hasNoMatch) {
      setShowIncompatibleContractModal(true);
      return;
    }
    onResolved?.(groupId, resolvedRosterId);
  }, [rosterLookupGroupId, rosterLoading, hasNoMatch, resolvedRosterId]);

  const requestSubmit = React.useCallback<(groupId: string, onResolved: ContractScopedSubmitHandler) => void>(
    (groupId, onResolved) => {
      pendingSubmitRef.current = onResolved;
      setRosterLookupGroupId(groupId);
    },
    []
  );

  const dismissIncompatibleContractModal = React.useCallback<() => void>(
    () => setShowIncompatibleContractModal(false),
    []
  );

  const reset = React.useCallback<() => void>(() => {
    setRosterLookupGroupId(undefined);
    pendingSubmitRef.current = null;
    setShowIncompatibleContractModal(false);
  }, []);

  return {
    requestSubmit,
    isResolvingRoster,
    showIncompatibleContractModal,
    dismissIncompatibleContractModal,
    reset,
  };
};

export default useContractScopedSubmit;
